<script setup lang="ts">
  defineOptions({
    name: 'AppSidebarNav',
  })

  import type { PropType } from 'vue'
  import { computed } from 'vue'

  import CPText from '@ui/components/CPText.vue'

  import AppSidebarNavItem from './AppSidebarNavItem.vue'

  import type { AppSidebarNavGroup, AppSidebarNavItemModel } from '../../types/app-sidebar-nav'

  const props = defineProps({
    ariaLabel: {
      type: String,
      required: true,
    },
    groups: {
      type: Array as PropType<AppSidebarNavGroup[]>,
      required: true,
    },
    activeItemId: {
      type: String,
      default: undefined,
    },
    isCollapsed: {
      type: Boolean,
      default: false,
    },
    inert: {
      type: Boolean,
      default: false,
    },
    groupHeaderIdPrefix: {
      type: String,
      default: 'app-sidebar-nav',
    },
  })

  const emit = defineEmits<{
    itemClick: [item: AppSidebarNavItemModel, event: MouseEvent]
  }>()

  const visibleGroups = computed(() => props.groups.filter(group => group.items.length > 0))

  const groupHeaderId = (group: AppSidebarNavGroup) => `${props.groupHeaderIdPrefix}-group-${group.id}`

  const isItemActive = (item: AppSidebarNavItemModel) => item.id === props.activeItemId

  const emitItemClick = (item: AppSidebarNavItemModel, event: MouseEvent) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return
    }

    emit('itemClick', item, event)
  }
</script>

<template>
  <nav
    class="app-sidebar-nav flex w-full flex-col gap-2 lg:gap-4 group data-[collapsible=icon]:gap-1"
    :class="{ 'pointer-events-none select-none': inert }"
    :aria-disabled="inert ? 'true' : undefined"
    :aria-label="ariaLabel"
    :inert="inert"
    :data-collapsible="isCollapsed ? 'icon' : 'content'"
  >
    <section
      v-for="group in visibleGroups"
      :key="group.id"
      class="app-sidebar-group w-full relative"
      :aria-labelledby="group.label ? groupHeaderId(group) : undefined"
    >
      <CPText
        v-if="group.label"
        :id="groupHeaderId(group)"
        size="sm"
        weight="medium"
        html-tag="h2"
        class="pl-[0.625rem] h-8 flex items-center sticky z-10 top-0 bg-sidebar mb-0 text-on-sidebar opacity-70 text-nowrap outline-hidden transition-[height,margin,padding,opacity] duration-200 ease-linear group-data-[collapsible=icon]:static group-data-[collapsible=icon]:z-0 group-data-[collapsible=icon]:pointer-events-none group-data-[collapsible=icon]:h-0 group-data-[collapsible=icon]:min-h-0 group-data-[collapsible=icon]:overflow-hidden group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:opacity-0"
      >
        <slot name="group-header" :group="group">
          {{ group.label }}
        </slot>
      </CPText>

      <ul class="m-0 w-full list-none p-0">
        <template v-for="item in group.items" :key="item.id">
          <slot
            name="item"
            :item="item"
            :is-active="isItemActive(item)"
            :is-collapsed="isCollapsed"
            :emit-click="(event: MouseEvent) => emitItemClick(item, event)"
          >
            <AppSidebarNavItem
              :to="item.to"
              :is-active="isItemActive(item)"
              :aria-label="item.ariaLabel"
              :disabled="item.disabled"
              @click="emitItemClick(item, $event)"
            >
              <template #leading>
                <slot name="item-leading" :item="item" :is-active="isItemActive(item)" />
              </template>
              <slot name="item-label" :item="item" :is-active="isItemActive(item)">
                {{ item.label }}
              </slot>
              <template #trailing>
                <slot name="item-trailing" :item="item" :is-active="isItemActive(item)" />
              </template>
            </AppSidebarNavItem>
          </slot>
        </template>
      </ul>
    </section>
  </nav>
</template>
